import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Send, ExternalLink, Loader2, CheckCircle2, X } from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import { useToast } from "../../hooks/use-toast";

const STATUS_STYLES = {
  Paid: "bg-[#E7F4EF] text-[#2E7157]",
  Sent: "bg-cream-2 text-coral",
  Draft: "bg-cream-2 text-forest",
  Overdue: "bg-[#FCE1DB] text-[#B92E14]",
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [xero, setXero] = useState({ connected: false, configured: false });
  const [loading, setLoading] = useState(true);
  const [pushingId, setPushingId] = useState(null);
  const { toast } = useToast();
  const [params] = useSearchParams();
  const clientFilter = params.get("client_id") || "";

  const load = async () => {
    const [i, c, x] = await Promise.all([
      api.invoicesList(), api.clients(),
      api.xeroStatus().catch(() => ({ connected: false, configured: false })),
    ]);
    setInvoices(i); setClients(c); setXero(x); setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const nameOf = (id) => clients.find((c) => c.id === id)?.name || "—";
  const filteredInvoices = clientFilter ? invoices.filter((i) => i.client_id === clientFilter) : invoices;
  const newInvoiceHref = clientFilter ? `/app/invoices/new?client_id=${clientFilter}` : "/app/invoices/new";
  const setStatus = async (inv, status) => {
    const updated = await api.updateInvoice(inv.id, { status });
    setInvoices(invoices.map((x) => (x.id === inv.id ? updated : x)));
  };

  const pushToXero = async (inv) => {
    setPushingId(inv.id);
    try {
      const res = await api.xeroPushInvoice(inv.id);
      setInvoices(invoices.map((x) => (x.id === inv.id
        ? { ...x, xero_invoice_id: res.xero_invoice_id, xero_url: res.xero_url, xero_status: res.xero_status }
        : x)));
      toast({ title: "Sent to Xero", description: `Draft invoice created in ${res.tenant_name}.` });
    } catch (ex) {
      toast({ title: "Couldn't send to Xero", description: apiErrorMessage(ex, "") });
    } finally { setPushingId(null); }
  };

  return (
    <div data-testid="invoices-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-sm text-[#6a736f]">Invoices</div>
          <h1 className="font-display text-4xl mt-1">
            {clientFilter ? `Invoices — ${nameOf(clientFilter)}` : "All invoices"}
          </h1>
        </div>
        <Link to={newInvoiceHref} data-testid="invoices-new-btn"
              className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium">
          <Plus size={16} /> New invoice
        </Link>
      </div>

      {clientFilter && (
        <div className="mt-4 bg-cream-2 border border-cream rounded-lg px-4 py-2.5 text-sm flex items-center justify-between gap-3" data-testid="invoices-client-filter-chip">
          <span className="text-forest">Showing invoices for <b>{nameOf(clientFilter)}</b> only.</span>
          <Link to="/app/invoices" data-testid="invoices-clear-filter"
                className="text-xs text-coral font-medium inline-flex items-center gap-1 hover:underline">
            <X size={12} /> Clear filter
          </Link>
        </div>
      )}

      {!xero.connected && xero.configured && (
        <div className="mt-4 bg-cream-2 border border-cream rounded-lg px-4 py-3 text-sm flex items-start justify-between gap-3">
          <div className="text-forest">
            <span className="font-medium">Xero not connected yet.</span> Connect it once and send each invoice to Xero as a Draft with one click.
          </div>
          <Link to="/app/settings" className="btn-outline-forest px-3 py-1.5 rounded-md text-xs font-medium shrink-0">Connect Xero</Link>
        </div>
      )}

      <div className="mt-6 bg-white rounded-xl border border-cream overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-2/50 text-xs text-[#6a736f]">
            <tr className="text-left">
              <th className="px-4 py-3">Invoice</th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Issued</th>
              <th className="px-4 py-3">Due</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-right">Xero</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EAF3EE]">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#6a736f]">Loading…</td></tr>
            ) : filteredInvoices.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#6a736f]" data-testid="invoices-empty">
                {clientFilter ? "No invoices for this client yet." : "No invoices yet. Create one from tracked hours."}
              </td></tr>
            ) : filteredInvoices.map((i) => (
              <tr key={i.id} className="hover:bg-cream-2/40" data-testid={`invoice-row-${i.id}`}>
                <td className="px-4 py-3 font-medium">{i.number}</td>
                <td className="px-4 py-3">{nameOf(i.client_id)}</td>
                <td className="px-4 py-3">{i.issued}</td>
                <td className="px-4 py-3">{i.due}</td>
                <td className="px-4 py-3">
                  <select value={i.status} onChange={(e) => setStatus(i, e.target.value)}
                    className={`text-xs px-2 py-1 rounded-full font-medium border-0 ${STATUS_STYLES[i.status]}`}>
                    <option>Draft</option><option>Sent</option><option>Paid</option><option>Overdue</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold">${i.total.toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  {i.xero_invoice_id ? (
                    <a href={i.xero_url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-[#2E7157] hover:underline">
                      <CheckCircle2 size={12} /> Open in Xero <ExternalLink size={11} />
                    </a>
                  ) : xero.connected ? (
                    <button onClick={() => pushToXero(i)} disabled={pushingId === i.id}
                      className="btn-outline-forest inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs">
                      {pushingId === i.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Send to Xero
                    </button>
                  ) : (
                    <span className="text-xs text-[#6a736f]">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
